import { DecimalPipe, NgClass, NgFor } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Host,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { FeedProfitCalculations } from 'src/app/feed-profit/services/feed-profit-calculations.service';

import { FeedProfitExtendedSimulationsCalculationService } from 'src/app/feed-profit/services/feed-profit-extended-simulations-calculation.service';

import { FeedProfitQuickSimulationsCalculationService } from 'src/app/feed-profit/services/feed-profit-quick-simulations-calculation.service';
import { Discount } from 'src/app/settings/models/settings.model';
import { Product } from 'src/app/shared/models/shared.model';

import {
  Calculation,
  ExtendedSimulation,
  Feeder,
  FeederTableRow,
  FeederType,
  GenderFeedRatio,
  GenderOptions,
  GrowFinisherTypes,
  PriceSetting,
} from '../../../../models/feed-profit.model';
import { BASELINE_TABLE_HOST } from '../baseline-feeders.tokens';

@Component({
  selector: 'app-baseline-calculated-table',
  templateUrl: './baseline-calculated-table.component.html',
  styleUrls: ['./baseline-calculated-table.component.scss'],
  imports: [TableComponent, MatSelect, NgClass, NgFor, MatOption],
})
export class BaselineCalculatedTableComponent implements OnInit, AfterViewInit {
  @Input() products: Product[];
  @Input() calculatedEndFeederRows: Feeder[];
  @Input() discounts: Discount;
  @Input() isExtendedSimulation: boolean;

  feederType = FeederType.END_FEEDER;
  productArray = new FormArray([]);
  barrowArray = new FormArray([]);
  boarArray = new FormArray([]);
  giltArray = new FormArray([]);
  tableConfig: TableConfig;
  tableConfigBase: TableConfig = {
    dataSource: new MatTableDataSource([] as FeederTableRow[]),
    columns: undefined,
    selectColumn: false,
    actionColumn: false,
    pagination: false,
    disableCards: true,
    enableArrowKeys: true,
    footer: false,
    scrolling: false,
    wrapperWidth: '100%',
  };
  private tableColumns: TableColumn[];
  private displayedTableColumns: TableColumn[];
  productError = false;

  @ViewChild('productNameTemplate') productNameTemplate: TemplateRef<Product>;
  @ViewChild('feedAmountTemplate') feedAmountTemplate: TemplateRef<unknown>;
  @ViewChild('feedAmountAverageTemplate') feedAmountAverageTemplate: TemplateRef<unknown>;
  @ViewChild('deleteIconTemplate') deleteIconTemplate: TemplateRef<unknown>;
  @Output()
  calculatedFeederPropertiesChanged: EventEmitter<Calculation> = new EventEmitter<Calculation>();

  constructor(
    @Optional()
    @Host()
    @Inject(BASELINE_TABLE_HOST)
    private baselineTableComponent: {
      baselineFeedersComponent?: {
        genders?: string;
        calculation?: Calculation;
        totalTableTotalAverages?: number;
      };
    },
    private changeDetectorRef: ChangeDetectorRef,
    private readonly feedProfitQuickSimCalculationService: FeedProfitQuickSimulationsCalculationService,
    private readonly feedProfitExtendedSimCalculationService: FeedProfitExtendedSimulationsCalculationService,
    private readonly locale: LocaleService,
    private readonly decimalPipe: DecimalPipe,
    private readonly calculationsService: FeedProfitCalculations,
  ) {}

  updateData(calculatedEndFeederRows: Feeder[]) {
    const initialDatasource: FeederTableRow[] = [];
    if (calculatedEndFeederRows?.length > 0) {
      calculatedEndFeederRows?.forEach((row, index) =>
        initialDatasource.push(this.getDatasourceRow(row, index)),
      );
    } else {
      // initial row, because the table should always show at least one row.
      initialDatasource.push({
        id: 0,
        boar: null,
        barrow: null,
        gilt: null,
      });
    }

    this.displayedTableColumns = this.tableColumns.filter(
      (col) =>
        (this.baselineTableComponent?.baselineFeedersComponent?.genders ===
        GenderOptions.BARROW_GILT
          ? col.id !== GrowFinisherTypes.BOAR
          : col.id !== GrowFinisherTypes.BARROW) &&
        (this.baselineTableComponent?.baselineFeedersComponent?.calculation?.companyDetails
          ?.priceSetting === PriceSetting.NETTO
          ? col.id !== 'priceGross'
          : col.id !== 'priceNet'),
    );
    this.tableConfig = <TableConfig>{
      ...this.tableConfigBase,
      columns: this.displayedTableColumns,
      dataSource: new MatTableDataSource<FeederTableRow>(initialDatasource),
    };
  }

  ngOnInit(): void {
    this.getTableColumns();
    this.updateData(this.calculatedEndFeederRows);
  }

  ngAfterViewInit() {
    const newConfig = { ...this.tableConfig };
    if (newConfig.columns) {
      const productColumn = newConfig.columns.find((c) => c.id === 'product');
      if (productColumn) {
        productColumn.template = this.productNameTemplate;
      }
      if (
        this.baselineTableComponent?.baselineFeedersComponent?.genders === GenderOptions.BOAR_GILT
      ) {
        const boarColumn = newConfig.columns.find((c) => c.id === GrowFinisherTypes.BOAR);
        if (boarColumn) {
          boarColumn.template = this.feedAmountTemplate;
        }
      } else {
        const barrowColumn = newConfig.columns.find((c) => c.id === GrowFinisherTypes.BARROW);
        if (barrowColumn) {
          barrowColumn.template = this.feedAmountTemplate;
        }
      }
      const giltColumn = newConfig.columns.find((c) => c.id === GrowFinisherTypes.GILT);
      if (giltColumn) {
        giltColumn.template = this.feedAmountTemplate;
      }
      const averageColumn = newConfig.columns.find((c) => c.id === 'average');
      if (averageColumn) {
        averageColumn.template = this.feedAmountAverageTemplate;
      }
      this.tableConfig = newConfig;
    }
    //subscribe to any feeder changes
    this.changeDetectorRef.detectChanges();
  }

  calculateFeedRatios(calculation: Calculation, boar?: number, gilt?: number, barrow?: number) {
    const tableRows = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign({}, row),
    );
    const currentFeeders =
      (this.isExtendedSimulation
        ? calculation?.extendedSimulation?.feeders
        : calculation?.feeders) || [];
    const ratioCalculation = {
      ...calculation,
      feeders: currentFeeders.map((feeder) => ({
        ...feeder,
        genderFeedRatios: feeder.genderFeedRatios.map((ratio) => ({
          ...ratio,
          feed: this.getRatioFeedValue(ratio.feed),
        })),
      })),
    };
    if (
      (!this.isExtendedSimulation &&
        (calculation === null ||
          calculation === undefined ||
          calculation?.extendedBaseLine === null ||
          calculation?.extendedBaseLine === undefined ||
          calculation?.extendedBaseLine?.slaughteredWeightInKg == null ||
          calculation?.extendedBaseLine?.startWeight == null ||
          calculation?.extendedBaseLine?.averageEnergyConversion == null ||
          calculation?.genders == null ||
          calculation?.country == null)) ||
      (this.isExtendedSimulation &&
        (calculation === null ||
          calculation === undefined ||
          calculation?.extendedSimulation === null ||
          calculation?.extendedSimulation === undefined ||
          calculation?.extendedBaseLine === null ||
          calculation?.extendedBaseLine === undefined ||
          calculation?.extendedSimulation?.extendedBaseLine === null ||
          calculation?.extendedSimulation?.extendedBaseLine === undefined ||
          calculation?.extendedBaseLine?.averageEnergyConversion == null ||
          calculation?.extendedSimulation?.extendedBaseLine?.averageEnergyConversion == null ||
          calculation?.extendedSimulation?.extendedBaseLine?.slaughteredWeightInKg == null ||
          calculation?.extendedSimulation?.extendedBaseLine?.startWeight == null ||
          calculation?.extendedSimulation?.genders == null ||
          calculation?.extendedSimulation?.country == null))
    ) {
      tableRows[0].barrow = null;
      tableRows[0].boar = null;
      tableRows[0].gilt = null;
      tableRows[0].average = 0;
      this.tableConfig.dataSource.data = tableRows;
      this.setOtherTablePercentage();
    } else {
      const feeders = this.isExtendedSimulation
        ? this.feedProfitExtendedSimCalculationService.calculateRemainingEndFeederRatios(
            ratioCalculation,
            ratioCalculation.extendedSimulation,
            undefined,
            undefined,
            this.discounts,
          )
        : this.feedProfitQuickSimCalculationService.calculateRemainingEndFeederRatios(
            ratioCalculation,
          );

      if (tableRows.length > 0) {
        tableRows[0].barrow = barrow || feeders.barrow;
        tableRows[0].boar = boar || feeders.boar;
        tableRows[0].gilt = gilt || feeders.gilt;
        tableRows[0].average = this.calculateAverageGenderFeedRatio(
          feeders.gilt,
          feeders.barrow,
          feeders.boar,
        );
        this.tableConfig.dataSource.data = tableRows;
        this.setOtherTablePercentage();
      }
    }
  }

  tableRowToFeeder(row: FeederTableRow) {
    const genderFeedRatios = <GenderFeedRatio[]>[];

    genderFeedRatios.push(<GenderFeedRatio>{
      name: GenderTypes.GILT,
      feed: row.gilt,
    });
    if (this.baselineTableComponent.baselineFeedersComponent.genders === GenderOptions.BOAR_GILT) {
      genderFeedRatios.push(<GenderFeedRatio>{
        name: GenderTypes.BOAR,
        feed: row.boar,
      });
    } else {
      genderFeedRatios.push(<GenderFeedRatio>{
        name: GenderTypes.BARROW,
        feed: row.barrow,
      });
    }
    return <Feeder>{
      type: this.feederType,
      product:
        row.product ||
        <Product>{
          id: row.product.id,
          code: row.product?.code,
          name: row.product?.name,
          grossPricePer100Kg: row.grossPricePer100Kg ? row.grossPricePer100Kg : 0,
          ewKg: row.ewKg ? row.ewKg : 0,
          lysKg: row.lysKg ? row.lysKg : 0,
          phosphorusGramPerKg: row.phosphorusGramPerKg ? row.phosphorusGramPerKg : 0,
          proteinGramPerKg: row.proteinGramPerKg ? row.proteinGramPerKg : 0,
          suitablePhases: [],
          energySystem: null,
          lysineSystem: null,
          schemeTypes: [],
        },
      genderFeedRatios,
    };
  }

  private getDatasourceRow(row: Feeder, index: number): FeederTableRow {
    const gilt = row.genderFeedRatios.find((p) => p.name === GenderTypes.GILT)?.feed;
    const barrow = row.genderFeedRatios.find((p) => p.name === GenderTypes.BARROW)?.feed;
    const boar = row.genderFeedRatios.find((p) => p.name === GenderTypes.BOAR)?.feed;
    return {
      id: index,
      product: row.product,
      grossPricePer100Kg: row.product.grossPricePer100Kg,
      unitNettoPrice: this.calculationsService.getProductPriceWithDiscounts(
        row.product.grossPricePer100Kg,
        this.discounts,
      ),
      ewKg: row.product.ewKg,
      lysKg: row.product.lysKg,
      phosphorusGramPerKg: row.product.phosphorusGramPerKg,
      proteinGramPerKg: row.product.proteinGramPerKg,
      gilt,
      barrow,
      boar,
      average: this.calculateAverageGenderFeedRatio(gilt, barrow, boar),
      percentage: null,
    };
  }

  private getTableColumns() {
    const getControls = (): FormControl[] => {
      const controls: FormControl[] = [];
      this.calculatedEndFeederRows?.forEach((_) => controls.push(new FormControl()));
      return controls;
    };
    this.productArray = new FormArray(getControls());
    this.barrowArray = new FormArray(getControls());
    this.boarArray = new FormArray(getControls());
    this.giltArray = new FormArray(getControls());

    this.tableColumns = [
      {
        id: 'product',
        type: TableColumnTypes.TEMPLATE,
        disableSort: true,
        inputConfig: {
          formArray: <FormArray>this.productArray,
        },
      },
      {
        id: 'grossPricePer100Kg',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
        digitsInfo: '1.02-2',
      },
      {
        id: 'unitNettoPrice',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
        digitsInfo: '1.02-2',
      },
      {
        id: 'ewKg',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
      },
      {
        id: 'lysKg',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
      },
      {
        id: 'phosphorusGramPerKg',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
      },
      {
        id: 'proteinGramPerKg',
        type: TableColumnTypes.NUMBER,
        disableSort: true,
      },
      {
        id: GrowFinisherTypes.BARROW,
        type: TableColumnTypes.TEMPLATE,
        disableSort: true,
      },
      {
        id: GrowFinisherTypes.BOAR,
        type: TableColumnTypes.TEMPLATE,
        disableSort: true,
      },
      {
        id: GrowFinisherTypes.GILT,
        type: TableColumnTypes.TEMPLATE,
        disableSort: true,
      },
      {
        id: 'average',
        type: TableColumnTypes.TEMPLATE,
        disableSort: true,
      },
      // Empty columns added so styling is equal to that of normal columns who have an
      // extra columns for the delete and percentages, which this feeder does not have.
      {
        id: 'emptyPercentageColumn',
        type: TableColumnTypes.STRING,
        label: '',
      },
      {
        id: 'emptyColumn',
        type: TableColumnTypes.STRING,
        label: '',
      },
    ];
    this.displayedTableColumns = this.tableColumns.filter((col) =>
      this.baselineTableComponent?.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT
        ? col.id !== GrowFinisherTypes.BOAR
        : col.id !== GrowFinisherTypes.BARROW &&
            this.baselineTableComponent?.baselineFeedersComponent?.calculation?.companyDetails
              ?.priceSetting === PriceSetting.NETTO
          ? col.id !== 'priceGross'
          : col.id !== 'priceNet',
    );
  }

  private calculateAverageGenderFeedRatio(gilt: number, barrow: number, boar: number): number {
    const secondGender =
      this.baselineTableComponent?.baselineFeedersComponent?.genders === GenderOptions.BOAR_GILT
        ? boar
        : barrow;
    return ((gilt ?? 0) + (secondGender ?? 0)) / 2;
  }

  setOtherTablePercentage() {
    const tableRows = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign({}, row),
    );
    this.tableConfig.dataSource.data = tableRows.map((row) => ({
      ...row,
      percentage:
        Math.round(
          (row.average /
            this.baselineTableComponent?.baselineFeedersComponent?.totalTableTotalAverages) *
            10000,
        ) / 100,
    }));
  }

  changeGenders(updatedGenders: string) {
    if (this.baselineTableComponent?.baselineFeedersComponent?.genders) {
      this.baselineTableComponent.baselineFeedersComponent.genders = updatedGenders;
    }
    this.displayedTableColumns = this.tableColumns.filter((col) =>
      this.baselineTableComponent?.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT
        ? col.id !== GrowFinisherTypes.BOAR
        : col.id !== GrowFinisherTypes.BARROW,
    );
    this.tableConfig = <TableConfig>{
      ...this.tableConfig,
      columns: this.displayedTableColumns,
    };
    if (this.calculatedEndFeederRows?.length > 0) {
      this.tableConfig.dataSource.data = this.calculatedEndFeederRows?.map(
        (row: Feeder, index: number) => ({
          ...this.getDatasourceRow(row, index),
        }),
      );

      this.calculateFeedRatios(this.baselineTableComponent?.baselineFeedersComponent?.calculation);
      this.updateSaveData();
    }
  }

  deleteTableRow(selectedRow: FeederTableRow) {
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data
        .filter((row: FeederTableRow) => row.id !== selectedRow.id)
        .sort((a: FeederTableRow, b: FeederTableRow) => (a.id > b.id ? 1 : -1)),
    ];
    this.updateSaveData();
  }

  areProductsEqual(p1: Product, p2: Product): boolean {
    if (!p1 || !p2) {
      return false;
    }
    return p1.id === p2.id && p1.code === p2.code;
  }

  getProduct(row: FeederTableRow, products: Product[]): Product {
    let foundProduct = <Product>{};
    if (row.product) {
      foundProduct = !!products && products.find((product) => product.code === row.product.code);
    }
    return foundProduct;
  }

  selectProduct(selection: MatSelectChange, tableRow: FeederTableRow) {
    const tableRows = this.tableConfig.dataSource.data.map((row: FeederTableRow) =>
      Object.assign({}, row),
    );

    this.tableConfig.dataSource.data = [
      ...tableRows.filter((row) => row.id !== tableRow.id),
      {
        ...tableRows.find((r) => r.id === tableRow.id),
        product: <Product>selection.value,
        grossPricePer100Kg: (<Product>selection.value).grossPricePer100Kg,
        unitNettoPrice: this.calculationsService.getProductPriceWithDiscounts(
          (<Product>selection.value).grossPricePer100Kg,
          this.discounts,
        ),
        ewKg: (<Product>selection.value).ewKg,
        lysKg: (<Product>selection.value).lysKg,
        phosphorusGramPerKg: (<Product>selection.value).phosphorusGramPerKg,
        proteinGramPerKg: (<Product>selection.value).proteinGramPerKg,
      },
    ];

    this.updateSaveData();
    this.calculateFeedRatios(this.baselineTableComponent?.baselineFeedersComponent?.calculation);
  }

  // update the selected calculation in the state with the data from this table,
  // so that the data can be saved from the component higher up (feed-profit-item.component)
  private updateSaveData() {
    const values = <Calculation>{
      ...this.baselineTableComponent?.baselineFeedersComponent?.calculation,
    };
    let calculatedFeeder: Feeder;
    this.tableConfig.dataSource.data.forEach((row: FeederTableRow, index: number) => {
      const genderFeedRatios = <GenderFeedRatio[]>[];

      genderFeedRatios.push(<GenderFeedRatio>{
        name: GenderTypes.GILT,
        feed: row.gilt,
      });

      if (
        this.baselineTableComponent?.baselineFeedersComponent?.genders === GenderOptions.BARROW_GILT
      ) {
        genderFeedRatios.push(<GenderFeedRatio>{
          name: GenderTypes.BARROW,
          feed: row.barrow,
        });
      } else {
        genderFeedRatios.push(<GenderFeedRatio>{
          name: GenderTypes.BOAR,
          feed: row.boar,
        });
      }
      calculatedFeeder = <Feeder>{
        id: index,
        type: this.feederType,
        product: <Product>{
          id: row.product?.id,
          code: row.product?.code,
          name: row.product?.name,
          grossPricePer100Kg: row.grossPricePer100Kg,
          unitNettoPrice: row.unitNettoPrice,
          ewKg: row.ewKg,
          lysKg: row.lysKg,
          phosphorusGramPerKg: row.phosphorusGramPerKg,
          proteinGramPerKg: row.proteinGramPerKg,
          suitablePhases: [],
          energySystem: null,
          lysineSystem: null,
          schemeTypes: [],
        },
        genderFeedRatios,
      };
    });
    if (this.isExtendedSimulation) {
      values.extendedSimulation = <ExtendedSimulation>{
        ...this.baselineTableComponent?.baselineFeedersComponent?.calculation?.extendedSimulation,
        calculatedFeeder,
      };
      if (
        !this.compareFeeders(
          calculatedFeeder,
          this.baselineTableComponent?.baselineFeedersComponent?.calculation?.extendedSimulation
            ?.calculatedFeeder,
        )
      ) {
        this.calculatedFeederPropertiesChanged.emit(values);
      }
    } else {
      values.calculatedFeeder = calculatedFeeder;
      if (
        !this.compareFeeders(
          calculatedFeeder,
          this.baselineTableComponent?.baselineFeedersComponent?.calculation?.calculatedFeeder,
        )
      ) {
        this.calculatedFeederPropertiesChanged.emit(values);
      }
    }
  }

  compareFeeders(feeder1: Feeder, feeder2: Feeder) {
    if (!feeder1 || !feeder2) {
      return feeder1 === feeder2;
    }

    const product1 = Object.entries(feeder1.product);
    const product2 = Object.entries(feeder2.product);

    return (
      feeder1?.type === feeder2?.type &&
      feeder1?.genderFeedRatios?.length === feeder1?.genderFeedRatios?.length &&
      feeder1?.genderFeedRatios?.every((ratio) => {
        const comparison = feeder1?.genderFeedRatios?.find((r) => r?.name === ratio?.name);
        return ratio?.name === comparison?.name && ratio?.feed === comparison?.feed;
      }) &&
      product1?.every((p1) => {
        const p2 = product2?.find((r) => r[0] === p1[0]);
        if (p1 && p2) {
          return p1[1] === p2[1];
        }
        return false;
      })
    );
  }

  getRatioFeedValue(value: number) {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return 0;
    }
    return value;
  }

  getValue(value: number, digitsInfo = '1.0-1') {
    if (value === undefined || value === null || isNaN(value) || !isFinite(value)) {
      return this.locale.translate('n/a');
    }
    return this.decimalPipe.transform(value, digitsInfo, this.locale.getLocale());
  }
}
